import { memo, useMemo, useState } from 'react'
import { UIEntryFieldItem } from '../store/schemas'
import ReferencedItem from './ReferencedItem'
import EditFieldPopover from './EditFieldPopover'
import EditedIndicatorPopover from '@/components/edited-indicator/EditedIndicatorPopover'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'
import clsx from 'clsx'
import { useAppSelector } from '@/store/store-hooks'
import { isCellBeingEdited } from '../utils/edit-helpers'
import { EditableEntryField } from '../editable-field-config'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'
import { useStartEditField } from '../hooks/useStartEditField'
import HoverableCellWrapper from '@/components/hoverable-cell-wrapper/HoverableCellWrapper'

/** Fields with citation bboxes — PDF highlights are shown for these. */
const HIGHLIGHT_ENABLED_FIELDS: Set<EditableEntryField> = new Set([
  EditableEntryField.AMOUNT,
  EditableEntryField.SERVICE_DATE,
  EditableEntryField.FACILITY_NAME,
  EditableEntryField.PROCEDURE_CODE,
])

interface ReferencedCellProps {
  item: UIEntryFieldItem
  rowId: string
  cellId: string
  scrollToCell: ScrollToCell
  entryId: string
  fieldKey: EditableEntryField
}

/**
 * Referenced Cell
 *
 * Table cell component for displaying a single referenced field for billing summary data.
 */
export default memo(function ReferencedCell(props: ReferencedCellProps) {
  const { item, rowId, cellId, scrollToCell, entryId, fieldKey } = props
  const { id: parentId } = useMedSummaryParent()
  const [cellHovered, setCellHovered] = useState(false)
  const [indicatorHovered, setIndicatorHovered] = useState(false)

  // Stable cellPosition object for downstream components (avoids new reference on every render)
  const cellPosition: CellPosition = useMemo(() => ({ rowId, cellId }), [rowId, cellId])

  // Hook for initiating edit mode
  const startEdit = useStartEditField({ entryId, fieldKey, originalValue: item.text ?? '' })

  const handleStartEdit = () => {
    setCellHovered(false) // reset so the popover doesn't reopen for this cell once editing ends
    startEdit()
  }

  // Check if this cell is currently being edited (boolean selector avoids re-renders on every keystroke)
  const isEditing = useAppSelector((state) => isCellBeingEdited(state.medSummaryEntryEditState.activeEdit, parentId, entryId, fieldKey))

  const isCurrency = EditableEntryField.AMOUNT === fieldKey
  const highlightsEnabled = HIGHLIGHT_ENABLED_FIELDS.has(fieldKey)

  return (
    <EditFieldPopover isOpen={isEditing} fieldKey={fieldKey}>
      <HoverableCellWrapper onCellHover={setCellHovered} onCellDoubleClick={handleStartEdit}>
        <div className={clsx('pl-3 py-[10.5px] overflow-hidden', isCurrency && 'pr-3')}>
          <ReferencedItem
            item={item}
            highlightsEnabled={highlightsEnabled}
            cellPosition={cellPosition}
            scrollToCell={scrollToCell}
            isCellHovered={cellHovered && !indicatorHovered}
            isCurrency={isCurrency}
            onStartEdit={handleStartEdit}
          />
          {/* Edited indicator - shows when field has been manually edited */}
          {item.updatedAt !== null && <EditedIndicatorPopover timestampMs={item.updatedAt} onIndicatorHoverChange={setIndicatorHovered} />}
        </div>
      </HoverableCellWrapper>
    </EditFieldPopover>
  )
})
